import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBoxOpen,
    faPuzzlePiece,
    faUserCircle,
    faCheckCircle,
    faPiggyBank,
} from '@fortawesome/free-solid-svg-icons';

const StatsCards = ({ sets, showPrices }) => {
    const totalSets = sets.reduce((acc, set) => acc + Number(set.quantity), 0);
    const totalUniqueModels = sets.length;
    const totalParts = sets.reduce((acc, set) => acc + (set.num_parts * Number(set.quantity)), 0);
    const completeCount = sets.reduce((acc, set) => acc + Number(set.complete), 0);
    const sealedCount = sets.reduce((acc, set) => acc + Number(set.sealed), 0);
    const totalOwnedMinifigs = sets.reduce((acc, set) => acc + (Number(set.owned_minifigures) || 0), 0);
    const totalExpectedMinifigs = sets.reduce((acc, set) => acc + (Number(set.expected_minifigures) || 0), 0);

    const totalValue = sets.reduce((acc, set) => {
        const sealed = Number(set.sealed) || 0;
        const complete = Number(set.complete) || 0;
        const nonSealedComplete = complete - sealed;
        const incomplete = Number(set.quantity) - complete;
        const sealedVal = parseFloat(set.sealed_value) || 0;
        const usedVal   = parseFloat(set.used_value)   || 0;
        const retail    = parseFloat(set.retail_price)  || 0;

        let value = 0;
        if (sealed           > 0) value += sealed           * (sealedVal > 0 ? sealedVal : retail);
        if (nonSealedComplete > 0) value += nonSealedComplete * (usedVal > 0 ? usedVal : retail);
        if (incomplete       > 0) value += incomplete       * (usedVal > 0 ? usedVal : retail);
        return acc + value;
    }, 0);

    const gridCols = showPrices
        ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5'
        : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4';

    return (
        <div className={`grid ${gridCols} gap-4 sm:gap-6 mb-8`}>
            {/* Total Sets */}
            <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-md hover:shadow-lg border border-gray-200 p-5 transition-all duration-200 hover:scale-105">
                <div className="flex items-start">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center text-white mr-4 shadow-lg">
                        <FontAwesomeIcon icon={faBoxOpen} className="text-lg" />
                    </div>
                    <div className="flex-grow">
                        <h3 className="text-gray-600 font-semibold text-sm uppercase tracking-wide mb-1">Total Sets</h3>
                        <p className="text-2xl font-bold text-gray-800 mb-1">{totalSets.toLocaleString()}</p>
                        <p className="text-xs text-gray-500 font-medium">{totalUniqueModels} unique models</p>
                    </div>
                </div>
            </div>

            {/* Total Pieces */}
            <div className="bg-gradient-to-br from-white to-blue-50 rounded-xl shadow-md hover:shadow-lg border border-blue-200 p-5 transition-all duration-200 hover:scale-105">
                <div className="flex items-start">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white mr-4 shadow-lg">
                        <FontAwesomeIcon icon={faPuzzlePiece} className="text-lg" />
                    </div>
                    <div className="flex-grow">
                        <h3 className="text-blue-700 font-semibold text-sm uppercase tracking-wide mb-1">Total Pieces</h3>
                        <p className="text-2xl font-bold text-gray-800 mb-1">{totalParts.toLocaleString()}</p>
                        <p className="text-xs text-blue-600 font-medium">Building bricks</p>
                    </div>
                </div>
            </div>

            {/* Total Minifigures */}
            <div className="bg-gradient-to-br from-white to-purple-50 rounded-xl shadow-md hover:shadow-lg border border-purple-200 p-5 transition-all duration-200 hover:scale-105">
                <div className="flex items-start">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center text-white mr-4 shadow-lg">
                        <FontAwesomeIcon icon={faUserCircle} className="text-lg" />
                    </div>
                    <div className="flex-grow">
                        <h3 className="text-purple-700 font-semibold text-sm uppercase tracking-wide mb-1">Total Minifigures</h3>
                        <div className="flex items-baseline gap-2 mb-1">
                            <span className="text-2xl font-bold text-purple-600">{totalOwnedMinifigs.toLocaleString()}</span>
                            <span className="text-lg text-gray-400">/</span>
                            <span className="text-lg font-semibold text-gray-600">{totalExpectedMinifigs.toLocaleString()}</span>
                        </div>
                        <p className="text-xs text-purple-600 font-medium">Collection minifigures</p>
                    </div>
                </div>
            </div>

            {/* Collection Status */}
            <div className="bg-gradient-to-br from-white to-emerald-50 rounded-xl shadow-md hover:shadow-lg border border-emerald-200 p-5 transition-all duration-200 hover:scale-105">
                <div className="flex items-start">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center text-white mr-4 shadow-lg">
                        <FontAwesomeIcon icon={faCheckCircle} className="text-lg" />
                    </div>
                    <div className="flex-grow">
                        <h3 className="text-emerald-700 font-semibold text-sm uppercase tracking-wide mb-1">Collection Status</h3>
                        <div className="flex items-baseline gap-2 mb-1">
                            <span className="text-2xl font-bold text-emerald-600">{completeCount.toLocaleString()}</span>
                            <span className="text-lg text-gray-400">/</span>
                            <span className="text-lg font-semibold text-gray-600">{totalSets.toLocaleString()}</span>
                        </div>
                        <p className="text-xs text-emerald-600 font-medium">{sealedCount.toLocaleString()} sealed sets</p>
                    </div>
                </div>
            </div>

            {/* Collection Value */}
            {showPrices && (
                <div className="bg-gradient-to-br from-white to-green-50 rounded-xl shadow-md hover:shadow-lg border border-green-200 p-5 transition-all duration-200 hover:scale-105">
                    <div className="flex items-start">
                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center text-white mr-4 shadow-lg">
                            <FontAwesomeIcon icon={faPiggyBank} className="text-lg" />
                        </div>
                        <div className="flex-grow">
                            <h3 className="text-green-700 font-semibold text-sm uppercase tracking-wide mb-1">Collection Value</h3>
                            <p className="text-2xl font-bold text-gray-800 mb-1">
                                ${totalValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            </p>
                            <p className="text-xs text-green-600 font-medium">Estimated market value</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StatsCards;
